import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, Tab, Button, Typography, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';

import { DropdownIcon } from 'assets/svgComponents/DropdownIcon';

import { useStyles } from '../styles';
import { OnboardingStatus } from 'models/general';
import { onboardingStatusToTabMap } from '../types';

interface WorkerStatusTabsProps {
  activeTab: number;
  workerCounts: {
    pending: number;
    processing: number;
    incomplete: number;
    completed: number;
  };
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const WorkerStatusTabs: React.FC<WorkerStatusTabsProps> = ({ activeTab, workerCounts, onTabChange }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabAnchorEl, setTabAnchorEl] = useState<null | HTMLElement>(null);

  const createTabLabel = (text: string, count: number) => (
    <Box className={classes.customTabLabel}>
      <Typography component="span">{text}</Typography>
      <Box className={`${classes.customCountBadge} custom-count-badge`}>{count < 9 ? `0${count}` : count}</Box>
    </Box>
  );

  const handleTabMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setTabAnchorEl(event.currentTarget);
  };

  const handleTabMenuClose = () => {
    setTabAnchorEl(null);
  };

  const handleTabMenuItemClick = (value: number) => {
    onTabChange({} as React.SyntheticEvent, value);
    handleTabMenuClose();
  };

  const tabOptions = [
    {
      value: OnboardingStatus.PENDING,
      key: 'pending',
      label: t('workerStatus.tabs.pending'),
      count: workerCounts?.[onboardingStatusToTabMap[OnboardingStatus.PENDING]],
    },
    {
      value: OnboardingStatus.PROCESSING,
      key: 'processing',
      label: t('workerStatus.tabs.processing'),
      count: workerCounts?.[onboardingStatusToTabMap[OnboardingStatus.PROCESSING]],
    },
    {
      value: OnboardingStatus.INCOMPLETE,
      key: 'incomplete',
      label: t('workerStatus.tabs.incomplete'),
      count: workerCounts?.[onboardingStatusToTabMap[OnboardingStatus.INCOMPLETE]],
    },
    {
      value: OnboardingStatus.COMPLETED,
      key: 'completed',
      label: t('workerStatus.tabs.completed'),
      count: workerCounts?.[onboardingStatusToTabMap[OnboardingStatus.COMPLETED]],
    },
  ];

  return (
    <Box className={classes.tabsContainer} data-testid="worker-status-tabs-container">
      {isMobile ? (
        <>
          <Button
            endIcon={<DropdownIcon />}
            className={classes.tabDropdown}
            data-testid="worker-status-tab-dropdown-button"
            disableRipple={true}
            onClick={handleTabMenuClick}
          >
            {tabOptions.find(option => option.value === activeTab)?.label}
          </Button>
          <Menu
            anchorEl={tabAnchorEl}
            open={Boolean(tabAnchorEl)}
            className={classes.dropdownMenu}
            disableScrollLock={true}
            disableRestoreFocus={true}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handleTabMenuClose}
          >
            {tabOptions.map(option => (
              <MenuItem
                key={option.value}
                data-testid={`worker-status-tab-dropdown-item-${option.key}`}
                onClick={() => handleTabMenuItemClick(option.value)}
                className={classes.dropdownMenuItem}
                selected={activeTab === option.value}
              >
                <Box className={classes.dropdownItemContent}>
                  <Typography component="span">{option.label}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <Tabs value={activeTab} onChange={onTabChange} className={classes.tabs} data-testid="worker-status-tabs">
          <Tab
            label={createTabLabel(t('workerStatus.tabs.pending'), workerCounts.pending)}
            className={classes.tab}
            data-testid="worker-status-tab-pending"
          />
          <Tab
            label={createTabLabel(t('workerStatus.tabs.processing'), workerCounts.processing)}
            className={classes.tab}
            data-testid="worker-status-tab-processing"
          />
          <Tab
            label={createTabLabel(t('workerStatus.tabs.incomplete'), workerCounts.incomplete)}
            className={classes.tab}
            data-testid="worker-status-tab-incomplete"
          />
          <Tab
            label={createTabLabel(t('workerStatus.tabs.completed'), workerCounts.completed)}
            className={classes.tab}
            data-testid="worker-status-tab-completed"
          />
        </Tabs>
      )}
    </Box>
  );
};

export default WorkerStatusTabs;
